import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { HtmlContent } from "@/components/RichText";
import { Slider } from "@/components/ui/slider";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { storePendingClaim } from "@/lib/accountStorage";

const byNumericOrder = (a, b) => Number(a.order || 0) - Number(b.order || 0);

export const orderQuestionsForQuestionnaire = (questions, sections) => {
  const orderedQuestions = [...(questions || [])].sort(byNumericOrder);
  const orderedSections = [...(sections || [])].sort(byNumericOrder);
  const knownSectionIds = new Set(orderedSections.map((section) => section.id));

  return [
    ...orderedSections.flatMap((section) => (
      orderedQuestions.filter((question) => question.section_id === section.id)
    )),
    ...orderedQuestions.filter((question) => (
      !question.section_id || !knownSectionIds.has(question.section_id)
    )),
  ];
};

export const sectionContextForQuestion = (sections, orderedQuestions, question) => {
  if (!question?.section_id) return null;
  const section = (sections || []).find((item) => item.id === question.section_id);
  if (!section) return null;
  const firstQuestion = (orderedQuestions || []).find((item) => item.section_id === question.section_id);
  return firstQuestion?.id === question.id ? section : null;
};

export function SectionIntro({ section, pick, lang }) {
  if (!section) return null;
  const title = pick(section, "title");
  const description = pick(section, "description");
  return (
    <section
      className="mb-6 rounded-2xl border border-sage-200 bg-sage-50/70 p-5 sm:p-6"
      data-testid="q-section-intro"
      aria-label={title || `${lang === "id" ? "Bagian" : "Section"} ${section.order}`}
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-teal-700">
        {lang === "id" ? "Bagian" : "Section"} {section.order}
      </div>
      {title && (
        <h2 className="mt-2 font-display text-xl leading-snug text-teal-900 sm:text-2xl" data-testid="q-section-title">
          {title}
        </h2>
      )}
      {description && (
        <HtmlContent
          html={description}
          className="mt-3 text-sm leading-relaxed text-teal-800/85 [&_p]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
        />
      )}
    </section>
  );
}

export function QuestionPrompt({ q, pick, lang }) {
  const description = pick(q, "description");
  return (
    <>
      <div className="mt-3 font-display text-2xl leading-snug md:text-3xl" role="heading" aria-level="2" data-testid="q-text">
        <HtmlContent html={pick(q, "text")} inline className="[&_p]:m-0 [&_p]:inline" />
        {q.required && <span className="ml-1 text-terracotta-500" aria-label={lang === "id" ? "Wajib" : "Required"} data-testid="q-required-marker">*</span>}
      </div>
      {description && (
        <HtmlContent
          html={description}
          className="mt-3 text-sm leading-relaxed text-muted-foreground [&_p]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
        />
      )}
    </>
  );
}

export function finishQuestionnaireSubmission(data, slug, navigate) {
  if (data?.anonymous_code && data?.claim_token) {
    storePendingClaim(data.anonymous_code, data.claim_token);
  }
  try {
    localStorage.removeItem(`se_session_${slug}`);
  } catch {}
  const resultPath = `/results/${encodeURIComponent(data.anonymous_code)}`;
  navigate(resultPath);
  return resultPath;
}

export default function Questionnaire() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session") || localStorage.getItem(`se_session_${slug}`);
  const { t, lang, pick } = useLang();
  const [inst, setInst] = useState(null);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();

  useEffect(() => { api.get(`/public/instruments/${slug}`).then(r => setInst(r.data)); }, [slug]);

  const questions = useMemo(
    () => orderQuestionsForQuestionnaire(inst?.questions, inst?.sections),
    [inst],
  );
  const total = questions.length;
  const q = questions[current];
  const sectionContext = sectionContextForQuestion(inst?.sections, questions, q);

  const setAns = (patch) => setAnswers((prev) => ({ ...prev, [q.id]: { question_id: q.id, ...(prev[q.id] || {}), ...patch } }));

  const persist = useCallback(async () => {
    if (!sessionId) return;
    setSaving(true);
    try {
      const list = Object.values(answers);
      await api.post(`/public/sessions/${sessionId}/answers`, { answers: list, lang });
      setSaved(true);
      setTimeout(() => setSaved(false), 1200);
    } finally { setSaving(false); }
  }, [answers, sessionId, lang]);

  useEffect(() => {
    if (Object.keys(answers).length === 0) return;
    const timer = setTimeout(persist, 600);
    return () => clearTimeout(timer);
  }, [answers, persist]);

  const validate = () => {
    if (!q.required) return true;
    const a = answers[q.id];
    if (!a) return false;
    if (q.question_type === "multiple_choice") return Array.isArray(a.value) && a.value.length > 0;
    if (q.question_type === "forced_choice") return isForcedChoiceComplete(q, a.value);
    if (q.question_type === "matrix") return a.value && Object.keys(a.value).length === (q.matrix_rows || []).length;
    if (q.question_type === "text") return a.value && String(a.value).trim().length > 0;
    if (q.question_type === "slider" || q.question_type === "numeric") return a.value !== null && a.value !== undefined && a.value !== "";
    return !!a.option_id;
  };

  const next = () => { if (!validate()) { alert(t("q.required")); return; } if (current + 1 < total) setCurrent(current + 1); };
  const prev = () => { if (current > 0) setCurrent(current - 1); };

  const submit = async () => {
    if (!validate()) { alert(t("q.required")); return; }
    setSubmitting(true);
    try {
      const list = Object.values(answers);
      const { data } = await api.post(`/public/sessions/${sessionId}/submit`, { answers: list, lang });
      finishQuestionnaireSubmission(data, slug, nav);
    } finally { setSubmitting(false); }
  };

  if (!inst || !q) return <div className="py-20 text-center text-muted-foreground">…</div>;
  const progress = ((current + 1) / total) * 100;
  const a = answers[q.id] || {};

  return (
    <div className="min-h-[70vh]">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="mb-8 space-y-3">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span data-testid="q-progress-text">{t("q.progress", { current: current + 1, total })}</span>
            <span>{saving ? "…" : saved ? (<span className="inline-flex items-center gap-1"><Check className="w-3 h-3" /> {t("q.autosave")}</span>) : null}</span>
          </div>
          <Progress value={progress} className="h-1.5" data-testid="q-progress-bar" />
        </div>
        {current === 0 && pick(inst, "instructions") && (
          <div className="mb-6 p-5 rounded-2xl border border-terracotta-200 bg-cream-100/60" data-testid="q-instructions-card">
            <div className="text-xs uppercase tracking-widest text-terracotta-700 font-semibold mb-2">{t("detail.instructions")}</div>
            <div className="text-sm text-teal-800/90 leading-relaxed [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-5 break-words">
              <HtmlContent html={pick(inst, "instructions")} />
            </div>
          </div>
        )}
        <SectionIntro section={sectionContext} pick={pick} lang={lang} />
        <div className="p-8 rounded-2xl bg-card border border-border animate-fade-up" key={q.id}>
          <div className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <span>{q.code}</span>
            <span className="px-1.5 py-0.5 rounded bg-secondary text-[10px] text-foreground/60">{q.question_type}</span>
          </div>
          <QuestionPrompt q={q} pick={pick} lang={lang} />
          <div className="mt-8">
            <QuestionRenderer q={q} a={a} setAns={setAns} pick={pick} lang={lang} />
          </div>
        </div>
        <div className="mt-6 flex items-center justify-between">
          <Button data-testid="q-prev-btn" variant="outline" onClick={prev} disabled={current === 0} className="rounded-full h-11 px-5">
            <ArrowLeft className="w-4 h-4 mr-1" /> {t("q.prev")}
          </Button>
          {current + 1 < total ? (
            <Button data-testid="q-next-btn" onClick={next} className="rounded-full h-11 px-6 bg-terracotta-500 hover:bg-terracotta-600 text-cream-50">
              {t("q.next")} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button data-testid="q-submit-btn" onClick={submit} disabled={submitting} className="rounded-full h-11 px-6 bg-teal-800 hover:bg-teal-700 text-cream-50">
              {t("q.submit")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function isForcedChoiceComplete(q, value) {
  const ids = new Set((q.matrix_rows || []).map((row) => row.id));
  return !!(value && value.most && value.least && value.most !== value.least && ids.has(value.most) && ids.has(value.least));
}

export function nextForcedChoiceValue(value, rowId, slot) {
  const current = value && typeof value === "object" ? value : {};
  const other = slot === "most" ? "least" : "most";
  const next = { ...current, [slot]: current[slot] === rowId ? undefined : rowId };
  if (next[slot] && next[other] === rowId) next[other] = undefined;
  return next;
}

function ForcedChoiceBlock({ q, a, setAns, pick, lang }) {
  const val = a.value && typeof a.value === "object" ? a.value : {};
  const labels = lang === "id"
    ? { hint: "Pilih satu yang paling mirip kamu dan satu yang paling nggak mirip kamu.", most: "Paling mirip aku", least: "Paling nggak mirip" }
    : { hint: "Pick the one that is most like you and the one that is least like you.", most: "Most like me", least: "Least like me" };
  const choose = (rowId, slot) => setAns({ value: nextForcedChoiceValue(val, rowId, slot), option_id: null });
  return (
    <div className="space-y-3" data-testid="q-forced-choice">
      <p className="text-xs text-muted-foreground">{labels.hint}</p>
      {(q.matrix_rows || []).map((row, ri) => {
        const isMost = val.most === row.id;
        const isLeast = val.least === row.id;
        return (
          <div
            key={row.id}
            data-testid={`q-fc-row-${ri}`}
            className={`p-4 rounded-xl border transition-colors ${isMost ? "border-terracotta-500 bg-terracotta-50" : isLeast ? "border-teal-700/40 bg-secondary" : "border-border bg-background"}`}
          >
            <div className="text-sm md:text-base leading-relaxed">{pick(row, "label")}</div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                aria-pressed={isMost}
                data-testid={`q-fc-most-${ri}`}
                onClick={() => choose(row.id, "most")}
                className={`h-9 px-4 rounded-full border text-xs font-medium transition-colors ${isMost ? "bg-terracotta-500 border-terracotta-500 text-cream-50" : "border-border hover:border-terracotta-300"}`}
              >
                {labels.most}
              </button>
              <button
                type="button"
                aria-pressed={isLeast}
                data-testid={`q-fc-least-${ri}`}
                onClick={() => choose(row.id, "least")}
                className={`h-9 px-4 rounded-full border text-xs font-medium transition-colors ${isLeast ? "bg-teal-800 border-teal-800 text-cream-50" : "border-border hover:border-sage-300"}`}
              >
                {labels.least}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function QuestionRenderer({ q, a, setAns, pick, lang }) {
  const type = q.question_type;

  if (type === "forced_choice") {
    return <ForcedChoiceBlock q={q} a={a} setAns={setAns} pick={pick} lang={lang} />;
  }

  if (type === "text") {
    return <Textarea data-testid="q-text-input" value={a.value || ""} onChange={(e) => setAns({ value: e.target.value })} rows={4} placeholder={lang === "id" ? "Tulis jawaban…" : "Type your answer…"} />;
  }

  if (type === "numeric") {
    return <Input data-testid="q-numeric-input" type="number" value={a.value ?? ""} onChange={(e) => setAns({ value: e.target.value })} className="h-11 max-w-xs" />;
  }

  if (type === "slider") {
    const min = Number(q.slider_min ?? 0), max = Number(q.slider_max ?? 10), step = Number(q.slider_step ?? 1);
    const val = a.value ?? Math.round((min + max) / 2);
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{min}</span>
          <span className="font-display text-2xl text-terracotta-700" data-testid="q-slider-value">{val}</span>
          <span className="text-muted-foreground">{max}</span>
        </div>
        <Slider data-testid="q-slider" min={min} max={max} step={step} value={[Number(val)]} onValueChange={(v) => setAns({ value: v[0] })} />
      </div>
    );
  }

  if (type === "dropdown") {
    return (
      <select
        data-testid="q-dropdown"
        value={a.option_id || ""}
        onChange={(e) => setAns({ option_id: e.target.value, value: null })}
        className="w-full h-11 rounded-lg border border-border bg-background px-3"
      >
        <option value="" disabled>{lang === "id" ? "Pilih…" : "Select…"}</option>
        {(q.options || []).map((o) => (<option key={o.id} value={o.id}>{pick(o, "label")}</option>))}
      </select>
    );
  }

  if (type === "multiple_choice") {
    const selected = Array.isArray(a.value) ? a.value : [];
    const toggle = (id) => {
      const next = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id];
      setAns({ value: next, option_id: null });
    };
    return (
      <div className="space-y-2">
        {(q.options || []).map((o, i) => {
          const on = selected.includes(o.id);
          return (
            <label
              key={o.id}
              data-testid={`q-mc-option-${i + 1}`}
              onClick={() => toggle(o.id)}
              className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${on ? "border-terracotta-500 bg-terracotta-50" : "border-border bg-background hover:border-sage-300 hover:bg-cream-50"}`}
            >
              <Checkbox checked={on} onCheckedChange={() => toggle(o.id)} />
              <span className="text-sm md:text-base leading-relaxed flex-1">{pick(o, "label")}</span>
            </label>
          );
        })}
      </div>
    );
  }

  if (type === "matrix") {
    const val = a.value || {};
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th></th>
              {(q.options || []).map((o) => (<th key={o.id} className="p-2 text-xs font-medium text-muted-foreground">{pick(o, "label")}</th>))}
            </tr>
          </thead>
          <tbody>
            {(q.matrix_rows || []).map((r, ri) => (
              <tr key={r.id} className="border-t border-border" data-testid={`q-matrix-row-${ri}`}>
                <td className="p-2 pr-4 text-sm">{pick(r, "label")}</td>
                {(q.options || []).map((o) => (
                  <td key={o.id} className="p-2 text-center">
                    <input
                      type="radio"
                      name={r.id}
                      checked={val[r.id] === o.id}
                      onChange={() => setAns({ value: { ...val, [r.id]: o.id } })}
                      data-testid={`q-matrix-cell-${ri}-${o.order}`}
                      className="w-4 h-4 accent-terracotta-500"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // likert / single_choice / yes_no
  const selected = a.option_id;
  return (
    <RadioGroup value={selected || ""} onValueChange={() => {}}>
      <div className="space-y-2">
        {(q.options || []).map((o) => {
          const isSel = selected === o.id;
          return (
            <label
              key={o.id}
              data-testid={`q-option-${o.order}`}
              onClick={() => setAns({ option_id: o.id, value: null })}
              className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${isSel ? "border-terracotta-500 bg-terracotta-50" : "border-border bg-background hover:border-sage-300 hover:bg-cream-50"}`}
            >
              <RadioGroupItem value={o.id} id={o.id} checked={isSel} />
              <span className="text-sm md:text-base leading-relaxed flex-1">{pick(o, "label")}</span>
            </label>
          );
        })}
      </div>
    </RadioGroup>
  );
}
